/**
 * -----------------------------------------------------------------------------
 * ACL Edit Form
 * -----------------------------------------------------------------------------
 * These components are used to render the role form for editing the role ACL details.
 */

import React, { FC, forwardRef, Ref, useEffect, useImperativeHandle, useRef, useState } from "react";

import { useOrganizationRoleCategoryACLListQuery, useOrganizationRoleCategoryListQuery } from "../../../api/network/adminApiServices";
import { ACLAccess, CategoryACL, TLegacyUserRoleACL, TRoleACLCategory } from "../../../api/types/UserRole";
import {
  AdminFormFieldIntermediateCheckbox,
  AdminFormFieldRadio,
  AdminFormFieldSubmit
} from "../../../components/admin/formFields";
import { useLoggedInUserData } from "../../../utils/user";
import Accordian from "../../../components/accordian";
import LoadingAnimation from "../../../assets/svg/loadingAnimation.svg";
import { useTranslation } from "react-i18next";
import { APP_CONFIG } from "../../../constants/constants";

interface RoleFormProps {
  values: any;
  errors: any;
  touched: any;
  handleChange: (event: React.ChangeEvent<any>) => void;
  formikSetValue: (field: string, value: any, shouldValidate?: boolean) => void;
  handleBlur: (event: React.FocusEvent<any>) => void;
  formikSetTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
  userCanEdit: boolean;
  loadingData: boolean;
}

interface CategoryAccessControlProps {
  category: TRoleACLCategory;
  role: string;
  accountId: string;
  values: any;
  formikSetValue: (field: string, value: any, shouldValidate?: boolean) => void;
  userCanEdit: boolean;
  ref?: Ref<any>;
}

export interface CategoryAccessControlRef {
  onResetClick: () => void;
}

const CategoryAccessControl = forwardRef<CategoryAccessControlRef, CategoryAccessControlProps>(({
  category,
  role,
  accountId,
  values,
  formikSetValue,
  userCanEdit,
}, ref) => {

  const { data: dataCategoryAcls, isFetching: isFetchingACLList } = useOrganizationRoleCategoryACLListQuery(
    {
      category: category.id,
      role: role,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
    }
  );

  const { results } = dataCategoryAcls || {};
  // Get all type of access levels from all roles
  const accessLevels = new Set<number>();
  results?.map((role) => (
    role.acl_access.map((item) => {
      accessLevels.add(item.access_level);
    })
  ));

  const [resultState,setResultState]=useState<CategoryACL[]>([]);

  useEffect(()=>{
    if(results) setResultState(results)
  },[results])

  const onResetClick = () => {
    let role_acls: TLegacyUserRoleACL[] = [...values.role_acls];
    results?.forEach((categoryACL: CategoryACL) => {
      const savedRoleAclIndex = role_acls?.findIndex((legacyRoleAcl: TLegacyUserRoleACL) => legacyRoleAcl.aclid.toLowerCase() === categoryACL.name.toLowerCase());
      const modifyACLObject = {
        accountid: accountId,
        roleid: role,
        aclid: categoryACL.name,
        accesslevel: categoryACL.acl_access.map((item) => item.is_default ? item.access_level : null).filter((item) => item !== null)[0] as 0 | 1 | 2 | 3,
      }
      setResultState((prev) => {
        return prev.map((acl) => {
          if (acl.id === categoryACL.id) {
            // Update access_level for the matching ACL
            return { ...acl, access_level: categoryACL.acl_access.map((item) => item.is_default ? item.access_level : null).filter((item) => item !== null)[0] as 0 | 1 | 2 | 3 };
          }
          return acl; // Keep other ACLs unchanged
        });
      });
      if (savedRoleAclIndex > -1) {
        role_acls[savedRoleAclIndex] = modifyACLObject;
      } else {
        role_acls.push(modifyACLObject);
      }
    });
    formikSetValue("role_acls", role_acls);
  };

  /**
 * Expose the `onClick` function to the parent component.
 */
  useImperativeHandle(ref, () => ({
    onResetClick,
  }));

  // Sort the entries of access levels
  const sortedAccessLevels = Array.from(accessLevels).sort();
  interface SelectAllProps {
    values: any;
    roles: CategoryACL[] | undefined;
  }
  const SelectAllOption: FC<SelectAllProps> = ({ roles, values }) => {
    if (!roles) return null;

    // Get saved access level of the roles
    let isSameAccessLevelSaved = false
    let isIntermediateSelected = false;
    const savedSelectedLevels = new Set<number>(); // To determine unique level across roles
    roles.map((role) => {
      const roleAccessLevel = values?.role_acls?.find((role_acl: TLegacyUserRoleACL) => role_acl.aclid.toLowerCase() === role.name.toLowerCase());
      if (roleAccessLevel) {
        savedSelectedLevels.add(roleAccessLevel.accesslevel);
      } else {
        // If role is not saved, then add default level into set(To determine unique level across roles)
        role.acl_access.map((item) => {
          if (item.is_default) {
            savedSelectedLevels.add(item.access_level);
          }
        });
      }
      return null;
    });
    if (savedSelectedLevels.size === 1) { // If all roles have same access level
      isSameAccessLevelSaved = true;
    } else if (savedSelectedLevels.size > 1) { // If all roles have different access level
      isIntermediateSelected = true;
    }
    return (
      <div className="px-5 flex-grow flex items-center col-span-12 border-b-2 py-3 h-15" key={category.id}>
        <label className="min-w-64 max-w-64 text-sm" />
        {
          sortedAccessLevels.map((item: number, i: number) => {
            return (
              <div className="flex justify-start row-start" key={i}>
                <AdminFormFieldIntermediateCheckbox
                  disabled={!userCanEdit}
                  key={`${roles[0].name.toString()}_${item}`}
                  label={"Select All"}
                  name={`${roles[0].name.toString()}_${item}`}
                  id={`${roles[0].name.toString()}_${item}`}
                  type="radio"
                  value={item}
                  customWrapperClass="w-36 flex items-center bg-white py-2"
                  customLabelTextClass="ml-2 text-sm font-bold"
                  disabledClass="disabled:opacity-50"
                  checked={isSameAccessLevelSaved && savedSelectedLevels.has(item)}
                  intermediate={isIntermediateSelected && savedSelectedLevels.has(item)}
                  onChange={(e) => {
                    if (e.target.value) {
                      let role_acls: TLegacyUserRoleACL[] = [...values.role_acls];
                      results?.map((categoryACLItem: CategoryACL, i: number) => {
                        let isLevelExistForRole = false;
                        categoryACLItem.acl_access.map((level) => level.access_level === item ? isLevelExistForRole = true : null);
                        if (!isLevelExistForRole) return null; // If access level does not exist for role, skip to save

                        const savedRoleAclIndex = role_acls?.findIndex((legacyRoleAcl: TLegacyUserRoleACL) => legacyRoleAcl.aclid.toLowerCase() === categoryACLItem.name.toLowerCase());
                        const modifyACLObject = {
                          accountid: accountId,
                          roleid: role,
                          aclid: categoryACLItem.name,
                          accesslevel: item as 0 | 1 | 2 | 3,
                        }
                        setResultState((prev) => {
                          return prev.map((acl) => {
                            if (acl.id === categoryACLItem.id) {
                              // Update access_level for the matching ACL
                              return { ...acl, access_level: item as 0 | 1 | 2 | 3 };
                            }
                            return acl; // Keep other ACLs unchanged
                          });
                        });
                        if (savedRoleAclIndex > -1) {
                          role_acls[savedRoleAclIndex] = modifyACLObject;
                        } else {
                          role_acls.push(modifyACLObject);
                        }
                      });
                      formikSetValue("role_acls", role_acls);
                    }
                  }}
                />
              </div>
            )
          })}
      </div>
    )
  }

  return (
    <div>
      {
        isFetchingACLList && (
          <div className="col-span-12 h-20 flex items-center justify-center bg-opacity-90">
            <img src={LoadingAnimation} alt="Loading animation" className="w-12 h-12" />
          </div>
        )
      }
      <SelectAllOption
        roles={results && results?.length > 0 ? results : undefined}
        values={values}
      />
      <div className="max-h-72 overflow-auto">
        {
          resultState?.map((categoryACL: CategoryACL, i: number) => {
            const savedRoleAclIndex = values?.role_acls?.findIndex((legacyRoleAcl: TLegacyUserRoleACL) => legacyRoleAcl.aclid.toLowerCase() === categoryACL.name.toLowerCase());
            const savedRoleAclItem = savedRoleAclIndex > -1 ? values?.role_acls[savedRoleAclIndex] : null;
            return (
              <div className="px-5 flex-grow flex items-center col-span-12 border-b-2 py-3 h-15 " key={categoryACL.id}>
                <label className="min-w-64 max-w-64 text-sm flex flex-col">
                  <span>{categoryACL.description}</span>
                  {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (<span className="text-heading-gray888 text-xs">{categoryACL.name}{` (${categoryACL.access_level})`}</span>)}
                </label>
                <div className="flex justify-start row-start grow">
                  {
                    sortedAccessLevels.map((accessLevel: number) => {
                      const item = categoryACL.acl_access.find((aclItem: ACLAccess) => aclItem.access_level === accessLevel ? aclItem : null);
                      if (item) {
                        return (
                          <AdminFormFieldRadio
                            disabled={!userCanEdit}
                            key={`${categoryACL.name.toString()}_${item.access_level}_${item.id}̦`} // key={item.id}
                            label={item.custom_access_level_name}
                            name={item.id.toString()}
                            id={item.id.toString()}
                            subText={item.is_default ? "(Default)" : undefined}
                            error="hi"
                            type="radio"
                            value={item.access_level}
                            customWrapperClass="w-36 col-span-2 flex items-center bg-white py-2 rounded-xl"
                            customLabelTextClass="ml-2 text-sm font-bold"
                            disabledClass="disabled:opacity-50"
                            // checked={savedRoleAclItem ? item.access_level === savedRoleAclItem?.accesslevel : item.is_default} // (12/3/2024) this old method of checking previously saved values is not needed anymore because the new categorized ACL APIs are giving correct/applicable values
                            checked={item.access_level === categoryACL.access_level} // (12/3/2024) this new method is the way-to-go because the new categorized ACL APIs are now giving correct/applicable values
                            onChange={(e) => {
                              if (e.target.value) {
                                const newAccessLevel = parseInt(e.target.value) as 0 | 1 | 2 | 3;
                                const modifyACLObject = {
                                  accountid: accountId,
                                  roleid: role,
                                  aclid: categoryACL.name,
                                  accesslevel: parseInt(e.target.value) as 0 | 1 | 2 | 3,
                                  
                                }
                                setResultState((prev) => {
                                  return prev.map((acl) => {
                                    if (acl.id === categoryACL.id) {
                                      // Update access_level for the matching ACL
                                      return { ...acl, access_level: newAccessLevel };
                                    }
                                    return acl; // Keep other ACLs unchanged
                                  });
                                });
                                let role_acls = [...values.role_acls];
                                if (savedRoleAclIndex > -1) {
                                  role_acls[savedRoleAclIndex] = modifyACLObject;
                                } else {
                                  role_acls.push(modifyACLObject);
                                }
                                formikSetValue("role_acls", role_acls);
                              }
                            }}
                          />
                        )
                      } else {
                        return (
                          <div className="w-36 inline-flex items-center bg-white py-2 self-center">
                            <span className="block text-center w-16 ml-5">-</span>
                          </div>
                        );
                      }
                    })
                  }
                </div>
              </div>
            )
          })
        }
      </div>
    </div>
  );
}
)

export const RoleAccessControlForm: FC<RoleFormProps> = ({
  values,
  errors,
  touched,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
}) => {
  // prepare group query params
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId")
  const { t } = useTranslation("translation", { keyPrefix: "admins.roles.detailsPage.form" });
  const categoryRef = useRef<CategoryAccessControlRef>(null);

  // fetch organization roles
  const {
    data: dataOrgRolesCategory,
    isFetching: isFetchingOrgRolesCategory,
  } = useOrganizationRoleCategoryListQuery({
    page: 1,
    page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
  });
  const { results } = dataOrgRolesCategory || {};

  return (
    <div className="px-5 pt-4 pb-8 bg-white w-full overflow-y-auto">
      {
        isFetchingOrgRolesCategory && (
          <div className="col-span-12 h-20 flex items-center justify-center bg-opacity-90">
            <img src={LoadingAnimation} alt="Loading animation" className="w-12 h-12" />
          </div>
        )
      }
      {
        results?.map((item: TRoleACLCategory, index: number) => {
          return (
            <Accordian title={
              <div className="flex justify-between w-full">
                <span>{item.name}</span>
                <AdminFormFieldSubmit
                  type="button"
                  variant="danger"
                  label={t("reset_default")}
                  onClick={() => {
                    if (userCanEdit) {
                      categoryRef.current?.onResetClick();
                    }
                  }}
                  disabled={results.length === 0 || loadingData || !userCanEdit}
                  customWrapperClass="pr-20"
                />
              </div>
            }
              collapsible={true}
              key={item.id}
              isMultiClick={true}
            >
              <CategoryAccessControl
                accountId={thisUserOrganizationId || ""}
                category={item}
                role={values.role_id}
                values={values}
                formikSetValue={formikSetValue}
                key={item.name}
                userCanEdit={userCanEdit}
                ref={categoryRef}
              />
            </Accordian>
          )
        })
      }
    </div>
  );
};